import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import {
  facilityFormSchema,
  facilityRoleValues,
  type Facility,
  type FacilityFormInput,
} from './types';
import { useCompanyOptions, useCreateFacility, useUpdateFacility } from './useFacilities';
import type { CompanyOption } from './types';

const EMPTY_COMPANIES: CompanyOption[] = [];

const companyLabel = (company: CompanyOption, t: ReturnType<typeof useTranslation>['t']) =>
  `${company.display_name} · ${t(`efs.settings.companies.types.${company.party_type}`, {
    ns: 'settings',
    defaultValue: company.party_type,
  })}`;

interface FacilitiesFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row?: Facility | null;
}

export const FacilitiesFormDialog = ({ open, onOpenChange, row }: FacilitiesFormDialogProps) => {
  const { t } = useTranslation();
  const companies = useCompanyOptions();
  const create = useCreateFacility();
  const update = useUpdateFacility();
  const isEdit = !!row;
  const companyRows = companies.data ?? EMPTY_COMPANIES;

  const form = useForm<FacilityFormInput>({
    resolver: zodResolver(facilityFormSchema),
    defaultValues: {
      company_id: '',
      facility_name: '',
      address: '',
      facility_role: 'receiving',
      active_flag: true,
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset({
        company_id: '',
        facility_name: '',
        address: '',
        facility_role: 'receiving',
        active_flag: true,
      });
      return;
    }

    if (row) {
      form.reset({
        company_id: row.company_id,
        facility_name: row.facility_name,
        address: row.address ?? '',
        facility_role: row.facility_role,
        active_flag: row.active_flag,
      });
      return;
    }

    form.reset({
      company_id: companyRows[0]?.company_id ?? '',
      facility_name: '',
      address: '',
      facility_role: 'receiving',
      active_flag: true,
    });
  }, [open, row, companyRows, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (isEdit && row) {
      await update.mutateAsync({
        id: row.facility_id,
        body: {
          facility_name: values.facility_name,
          address: values.address?.trim() || undefined,
          facility_role: values.facility_role,
          active_flag: values.active_flag,
        },
      });
    } else {
      await create.mutateAsync({
        company_id: values.company_id,
        facility_name: values.facility_name.trim(),
        address: values.address?.trim() || undefined,
        facility_role: values.facility_role,
      });
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || update.isPending;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEdit
              ? t('efs.settings.facilities.edit', { ns: 'settings' })
              : t('efs.settings.facilities.create', { ns: 'settings' })}
          </SheetTitle>
          <SheetDescription>
            {t('efs.settings.facilities.create_subtitle', { ns: 'settings' })}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            {!isEdit ? (
              <FormField
                control={form.control}
                name="company_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.settings.facilities.fields.company', { ns: 'settings' })}
                    </FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t('efs.settings.facilities.fields.company_placeholder', {
                              ns: 'settings',
                            })}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {companyRows.map((company) => (
                          <SelectItem key={company.company_id} value={company.company_id}>
                            {companyLabel(company, t)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {companyRows.length === 0 ? (
                      <p className="text-xs text-efs-text-muted">
                        {t('efs.settings.facilities.company_required_hint', { ns: 'settings' })}
                      </p>
                    ) : null}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            <FormField
              control={form.control}
              name="facility_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.settings.facilities.fields.facility_name', { ns: 'settings' })}
                  </FormLabel>
                  <FormControl>
                    <Input autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="facility_role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.settings.facilities.fields.facility_role', { ns: 'settings' })}
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {facilityRoleValues.map((role) => (
                        <SelectItem key={role} value={role}>
                          {t(`efs.settings.facilities.roles.${role}`, { ns: 'settings' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.settings.facilities.fields.address', { ns: 'settings' })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending || (!isEdit && companyRows.length === 0)}>
                {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isEdit
                  ? t('efs.common.actions.save')
                  : t('efs.settings.facilities.actions.create', { ns: 'settings' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
